import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { tabulatorGet } from '../../api/ApiController';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import { Dialog } from 'primereact/dialog';
import { PanelMenu } from 'primereact/panelmenu';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import { EditIcon, PrintIcon, SearchIcon, ExportIcon, RefreshIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, EditRowIcon } from "../svg/svgIcons";
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { MaintenanceMaskDialogCheckbox } from '../../components/maintenance/MaintenanceMaskDialogCheckbox';
import { OverflowMenuProvider } from '../../components/toolbar/OverflowMenu';
import { showExcludedMachinesInLists } from '../../utils/moduleSettings';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import { buildNavigationContextFromTable } from '../../utils/maintenanceNavigationContext';
import { useTabulatorScrollArrows } from '../../utils/useTabulatorScrollArrows';
import { primeMultiSelectHeaderFilter } from '../../utils/primeMultiSelectHeaderFilter';
import { getEffectiveMask } from '../../utils/effectiveMask';
import {
  buildMaintenanceMaskAkcieSubItems,
  createConfirmSaveStandardMask,
  createConfirmApplyStandardLayoutToUserMask,
  createSaveUserMaskColumns,
  createOpenMaskDialog,
  createMaskSetFieldHandler,
  createMaskSelectAllHandler,
  createMaskDeselectAllHandler,
  hasApplicableStandardMaskRow,
  MAINTENANCE_MASK_DIALOG_SKIP_FIELDS,
} from '../../utils/maintenanceTabulatorMaskActions';
import {
  cleanSorters,
  extractSafePaginationParams,
  createDedupedAjaxRequest,
  refreshProgressiveFromStart,
  safeParse,
  applyUserMaskRowToTabulator,
  mergePagedStandardListFiltersAndSorters,
  appendMaintenanceRecordListPagedFilter,
  sanitizeTabulatorLayoutAttributes,
  TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS,
} from '../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../utils/tabulatorPersistLayout';
import { inputOnEnterHeaderFilter } from '../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle } from '../../utils/tabulatorColumnMinWidth';
import { buildTabulatorIdColumn } from '../../utils/tabulatorIdColumn';
import { buildAuditTabulatorColumns, mergeColumnVisibilityWithAuditDefaults } from '../../utils/tabulatorAuditColumns';
import { LocaleContext } from '../../components/LocaleContext';
import { defaultProductLogoSrc } from '../../constants/brandingDisplay';
import { applyTabulatorRuntimeI18n } from '../../utils/tabulatorRuntimeI18n';
import {
  buildCanonicalPrintFooterHtml,
  buildCanonicalPrintHeaderHtml,
} from '../../utils/printChrome';
const MAINTENANCE_NEED_RECORD_LIST_COLUMN_I18N_KEYS = Object.freeze({
  'name': 'Common.MachineName',
  'machine_type.name': 'MachineList.Column.MachineTypeName',
  'serial_number': 'Common.SerialNumber',
  'fabrication_year': 'Common.FabricationYear',
  'last_overhaul': 'Common.LastOverhaul',
  'description': 'Common.MachineDescription',
  'manufacturer': 'Common.Manufacturer',
  'manufacturer_tel_number': 'Common.Column.Phone',
  'activity': 'Common.OverallStatus',
  'rowSelection': 'Common.MachineName',
  'type': 'MachineList.Column.Type',
  'manufacturer_address': 'BusinessPartnerList.Column.Address',
  'manufacturer_fax': 'BusinessPartnerList.Column.Fax',
  'manufacturer_email': 'Common.Email',
  'manufacturer_web': 'Common.Web',
});



const MASK_PAGE = 'MaintenanceNeedRecordList';

const DEFAULT_NEED_RECORD_LIST_COLUMN_VISIBILITY = Object.freeze({
  id: true,
  name: true,
  'machine_type.name': true,
  type: true,
  serial_number: true,
  fabrication_year: true,
  last_overhaul: true,
  description: true,
  manufacturer: true,
  manufacturer_address: true,
  manufacturer_tel_number: true,
  manufacturer_fax: true,
  manufacturer_email: true,
  manufacturer_web: true,
  activity: true,
  created_at: true,
  updated_at: true,
});

function mergeNeedRecordListColumnVisibilityFromSaved(saved) {
  return mergeColumnVisibilityWithAuditDefaults(DEFAULT_NEED_RECORD_LIST_COLUMN_VISIBILITY, saved);
}

function applyNeedRecordListDataColumnVisibility(table, visibility) {
  if (!table) return;
  for (const field of Object.keys(DEFAULT_NEED_RECORD_LIST_COLUMN_VISIBILITY)) {
    if (visibility[field] === false) table.hideColumn?.(field);
    else table.showColumn?.(field);
  }
}

export default function MaintenanceNeedRecordList() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const {
    openFavoriteDialog,
    toast,
    setFastView,
    setUserMaskFilter,
    setStandardMaskFilter,
    openChosenNotificationPreferences,
    getDateFormat,
    getUserColor,
    getUserInitials,
  } = useContext(UtilContext);

  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [favoriteIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);

  const [columnsVisible, setColumnsVisible] = useState(() => ({ ...DEFAULT_NEED_RECORD_LIST_COLUMN_VISIBILITY }));
  const [maskVisible, setMaskVisible] = useState(false);
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const ref = useRef(null);
  const elementRef = useRef(null);
  const overlayRef = useRef(null);
  const inFlightRef = useRef({ sig: null, promise: null });
  const completedCacheRef = useRef(new Map());
  const tableBuiltRef = useRef(false);
  const isRestoringRef = useRef(false);
  const savedHScrollRef = useRef({ left: 0, pending: false });

  const pageSizePc = useMemo(() => {
    const v = (state.module_settings || []).find((s) => s.module === 'global' && s.key === 'page_size_pc')?.value;
    const n = parseInt(v, 10);
    return (Number.isFinite(n) && n > 0) ? n : 50;
  }, [state.module_settings]);

  const dateFormatGlob = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFormatGlob = `${dateFormatGlob} HH:mm`;
  const canSetStandardMask = user?.permissions?.includes('set standard mask');
  const showExcludedMachines = showExcludedMachinesInLists(state.module_settings);

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
  const refreshFromStart = useCallback(
    () => refreshProgressiveFromStart(getTable, completedCacheRef, t('Common.LoadingEllipsis')),
    [getTable]
  );

  const captureHScroll = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    if (!holder) return;
    savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
  }, [getTable]);

  const restoreHScroll = useCallback(() => {
    if (!savedHScrollRef.current.pending) return;
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    const header = table.columnManager?.contentsElement;
    if (!holder) return;
    const left = savedHScrollRef.current.left;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        holder.scrollLeft = left;
        if (header) header.scrollLeft = left;
        savedHScrollRef.current.pending = false;
      });
    });
  }, [getTable]);

  const buildRequestParams = useCallback(
    (paramsFromTabulator = {}) => {
      const extra = { ...extractSafePaginationParams(paramsFromTabulator) };
      if (extra.size == null) extra.size = pageSizePc;
      extra.show_excluded_machines = showExcludedMachines ? '1' : '0';
      mergePagedStandardListFiltersAndSorters(extra, {
        getTable,
        tableBuiltForParams: tableBuiltRef.current,
        userMaskFilters: state.user_mask_filters,
        maskPage: MASK_PAGE,
        appendFilter: appendMaintenanceRecordListPagedFilter,
      });
      return extra;
    },
    [getTable, pageSizePc, showExcludedMachines, state.user_mask_filters]
  );

  const normalizeText = (value) =>
    (value ?? '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  function searchTable() {
    const table = getTable();
    if (!table) return;
    const col = table.getColumn?.('name');
    if (col) {
      const v = searchValue?.trim() ?? '';
      col.setHeaderFilterValue?.(v);
    }
    setVisibleSearch(false);
    captureHScroll();
    refreshFromStart();
  }

  const onSelectRow = () => {
    setEditIcon(true);
    setCopyIcon(true);
    setDeleteIcon(true);
    const sel = getTable()?.getSelectedData?.() ?? [];
    if (sel.length > 0) {
      if (sel.length === 1) {
        if (user.permissions?.includes('add maintenance')) setCopyIcon(false);
        if (user.permissions?.includes('edit maintenance')) setEditIcon(false);
      }
      if (user.permissions?.includes('delete maintenance')) setDeleteIcon(false);
    }
  };

  function textHeaderFilter(headerValue, rowValue) {
    if (!headerValue) return true;
    return normalizeText(rowValue).includes(normalizeText(headerValue));
  }

  function onEditRow(machine) {
    const navigationContext = buildNavigationContextFromTable({
      table: getTable(),
      cleanSorters,
      extra: {
        show_excluded_machines: showExcludedMachines ? '1' : '0',
      },
    });
    dispatch({
      type: 'create_new_tab', name: t('Print.Title.MaintenanceNeedRecordDetail'), translationKey: 'Print.Title.MaintenanceNeedRecordDetail', legacyLabel: t('Print.Title.MaintenanceNeedRecordDetail'),
      page: 'MaintenanceNeedRecordDetail',
      props: { machine, type: 'edit', not_last_used: true, navigationContext },
      mask: 'mntn031',
    });
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => onEditRow(rowData)}>
          <EditRowIcon />
        </button>
      </div>
    );
  }

  function MachineTypeFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const name = rowData?.machine_type?.name ?? '';
    return (
      <span className="block w-full min-w-0 truncate whitespace-nowrap" title={name || undefined}>
        {name}
      </span>
    );
  }

  const auditTailColumns = useMemo(
    () =>
      buildAuditTabulatorColumns({
        includeId: false,
        dateFormat: dateFormatGlob,
        dateTimeFormat: dateTimeFormatGlob,
        getUserColor,
        getUserInitials,
        textHeaderFilter,
        remotePassthrough: true,
      }),
    [dateFormatGlob, dateTimeFormatGlob, getUserColor, getUserInitials, textHeaderFilter]
  );

  const machineTypeFilterOptions = useMemo(
    () => (state.machine_types || []).map((mt) => ({ label: mt.name, value: mt.name })),
    [state.machine_types]
  );

  const columns = useMemo(
    () => [
      {
        title: '',
        field: 'rowSelection',
        formatter: 'rowSelection',
        titleFormatter: 'rowSelection',
        hozAlign: 'center',
        width: 44,
        headerSort: false,
        resizable: false,
        frozen: true,
        print: false,
        download: false,
      },
      {
        title: '',
        field: 'editor',
        hozAlign: 'center',
        width: 44,
        minWidth: 44,
        headerSort: false,
        formatter: reactFormatter(<EditRowFormatter />),
        resizable: false,
        frozen: true,
        print: false,
        download: false,
      },
      buildTabulatorIdColumn({
        frozen: true,
        headerFilterFunc: textHeaderFilter,
      }),
      {
        title: t('Common.MachineName'),
        field: 'name',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.MachineName')),
        widthGrow: 1,
      },
      {
        title: t('MachineList.Column.MachineTypeName'),
        field: 'machine_type.name',
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.MachineTypeName'), 'multiselect'),
        widthGrow: 1,
        formatter: reactFormatter(<MachineTypeFormatter />),
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: { placeholder: t('Common.All'), valueType: 'string', options: machineTypeFilterOptions },
        headerFilterFunc: () => true,
      },
      {
        title: t('MachineList.Column.Type'),
        field: 'type',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle('Typ stroja'),
        widthGrow: 1,
      },
      {
        title: t('Common.SerialNumber'),
        field: 'serial_number',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.SerialNumber')),
        widthGrow: 1,
      },
      {
        title: t('Common.FabricationYear'),
        field: 'fabrication_year',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.FabricationYear')),
        widthGrow: 1,
      },
      {
        title: t('Common.LastOverhaul'),
        field: 'last_overhaul',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.LastOverhaul')),
        widthGrow: 1,
      },
      {
        title: t('Common.MachineDescription'),
        field: 'description',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.MachineDescription')),
        widthGrow: 1,
      },
      {
        title: t('Common.Manufacturer'),
        field: 'manufacturer',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Manufacturer')),
        widthGrow: 1,
      },
      {
        title: t('BusinessPartnerList.Column.Address'),
        field: 'manufacturer_address',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle('Adresa'),
        widthGrow: 1,
      },
      {
        title: t('Common.Column.Phone'),
        field: 'manufacturer_tel_number',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Column.Phone')),
        widthGrow: 1,
      },
      {
        title: t('BusinessPartnerList.Column.Fax'),
        field: 'manufacturer_fax',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle('Fax'),
        widthGrow: 1,
      },
      {
        title: t('Common.Email'),
        field: 'manufacturer_email',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle('Email'),
        widthGrow: 1,
      },
      {
        title: t('Common.Web'),
        field: 'manufacturer_web',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle('Web'),
        widthGrow: 1,
      },
      {
        title: t('Common.OverallStatus'),
        field: 'activity',
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.OverallStatus'), 'multiselect'),
        widthGrow: 1,
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: {
          placeholder: t('Common.OverallStatus'),
          options: [
            { label: 'Aktívny', value: 'Aktívny' },
            { label: t('Status.Excluded'), value: 'Vyradený' },
          ],
        },
        headerFilterFunc: () => true,
      },
      ...auditTailColumns,
    ],
    [machineTypeFilterOptions, auditTailColumns, textHeaderFilter]
  );

  const { scrollToFirst, scrollUp, scrollDown, scrollToLast } = useTabulatorScrollArrows(getTable);

  function editEmployee() {
    const sel = getTable()?.getSelectedData?.()?.[0];
    if (sel) onEditRow(sel);
  }

  const itemRenderer = (item, options) => (
    <a className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
      {item.shortcut && <span className="ml-auto border-1 surface-border border-round surface-100 text-xs p-1">{item.shortcut}</span>}
    </a>
  );

  const [expandedKeys, setExpandedKeys] = useState({});
  const closeMenu = useCallback(() => {
    setTimeout(() => {
      OverflowMenuProvider.setVisible?.(null);
      setExpandedKeys({});
    }, 0);
  }, []);
  useEffect(() => {
    const table = typeof getTable === 'function' ? getTable() : null;
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: MAINTENANCE_NEED_RECORD_LIST_COLUMN_I18N_KEYS,
      placeholder: typeof t === 'function' ? t('Common.NoData') : undefined,
      reformatRows: true,
    });
  }, [locale, t, getTable]);


  const hasApplicableStandardMask = useMemo(
    () => hasApplicableStandardMaskRow(state.standard_mask_filters, MASK_PAGE, mergeNeedRecordListColumnVisibilityFromSaved),
    [state.standard_mask_filters]
  );

  const openMaskDialog = useCallback(
    createOpenMaskDialog({
      columnsVisible,
      setColumnsVisible,
      defaultColumnVisibility: DEFAULT_NEED_RECORD_LIST_COLUMN_VISIBILITY,
      mergeVisibilityFromSaved: mergeNeedRecordListColumnVisibilityFromSaved,
      setMaskVisible,
      closeMenu,
    }),
    [columnsVisible, closeMenu]
  );

  const saveUserMaskColumns = useCallback(
    createSaveUserMaskColumns({
      maskPage: MASK_PAGE,
      getTable,
      setUserMaskFilter,
      defaultColumnVisibility: DEFAULT_NEED_RECORD_LIST_COLUMN_VISIBILITY,
      columnsVisible,
      setColumnsVisible,
      setMaskVisible,
      toast,
    }),
    [columnsVisible, getTable, setUserMaskFilter, toast]
  );

  const confirmSaveStandardMask = useCallback(
    createConfirmSaveStandardMask({
      maskPage: MASK_PAGE,
      getTable,
      setStandardMaskFilter,
      defaultColumnVisibility: DEFAULT_NEED_RECORD_LIST_COLUMN_VISIBILITY,
      toast,
    }),
    [getTable, setStandardMaskFilter, toast]
  );

  const confirmApplyStandardLayoutToUserMask = useCallback(
    createConfirmApplyStandardLayoutToUserMask({
      maskPage: MASK_PAGE,
      state,
      setUserMaskFilter,
      defaultColumnVisibility: DEFAULT_NEED_RECORD_LIST_COLUMN_VISIBILITY,
      mergeVisibilityFromSaved: mergeNeedRecordListColumnVisibilityFromSaved,
      toast,
      closeMenu,
      onAppliedToTable: ({ colsForSave, attributes }) => {
        const table = getTable();
        if (!table) return;
        isRestoringRef.current = true;
        try {
          table.setColumnLayout?.(sanitizeTabulatorLayoutAttributes(attributes, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS));
        } catch (_) {}
        setColumnsVisible(colsForSave);
        applyNeedRecordListDataColumnVisibility(table, colsForSave);
        setTimeout(() => {
          isRestoringRef.current = false;
          table.redraw?.(true);
          captureHScroll();
          refreshFromStart();
        }, 0);
      },
    }),
    [state, closeMenu, getTable, setUserMaskFilter, toast]
  );

  const setMask = useCallback(
    createMaskSetFieldHandler({
      defaultColumnVisibility: DEFAULT_NEED_RECORD_LIST_COLUMN_VISIBILITY,
      columnsVisible,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyNeedRecordListDataColumnVisibility,
      toast,
    }),
    [columnsVisible, getTable, toast]
  );

  const maskSelectAll = useCallback(
    createMaskSelectAllHandler({
      defaultColumnVisibility: DEFAULT_NEED_RECORD_LIST_COLUMN_VISIBILITY,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyNeedRecordListDataColumnVisibility,
    }),
    [getTable]
  );

  const maskDeselectAll = useCallback(
    createMaskDeselectAllHandler({
      defaultColumnVisibility: DEFAULT_NEED_RECORD_LIST_COLUMN_VISIBILITY,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyNeedRecordListDataColumnVisibility,
    }),
    [getTable]
  );

  const handleClickOutside = (event) => {
    if (event.target.closest('[data-overflow-trigger]')) return;
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      setExpandedKeys({});
      OverflowMenuProvider.setVisible?.(null);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const items = [
    {
      key: '0',
      label: t('Common.Actions'),
      icon: 'pi pi-sliders-v',
      template: itemRenderer,
      items: buildMaintenanceMaskAkcieSubItems({
        maskPage: MASK_PAGE,
        getTable,
        setUserMaskFilter,
        cleanSorters,
        captureHScroll,
        refreshFromStart,
        closeMenu,
        openMaskDialog,
        openChosenNotificationPreferences,
        hasApplicableStandardMask,
        confirmApplyStandardLayoutToUserMask,
        itemRenderer,
      }),
    },
  ];

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="edit">
            <button className="disabled:opacity-25" disabled={editIcon} onClick={() => editEmployee()} type="button">
              <EditIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print">
            <button type="button" onClick={() => getTable()?.print?.(false, true)}>
              <PrintIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button type="button" onClick={() => setVisibleSearch(true)}>
              <SearchIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="export">
            <button type="button" onClick={() => getTable()?.download?.('xlsx', 'nakupy_stroja.xlsx', { sheetName: t('Print.Title.MaintenanceNeedRecordDetail') })}>
              <ExportIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh">
            <button type="button" onClick={() => { captureHScroll(); refreshFromStart(); }} title={t('Common.Refresh')}>
              <RefreshIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="first">
            <button type="button" onClick={() => scrollToFirst()}>
              <BackToFirstIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="back">
            <button type="button" onClick={() => scrollUp()}>
              <BackIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="forward">
            <button type="button" onClick={() => scrollDown()}>
              <ForwardIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="last">
            <button type="button" onClick={() => scrollToLast()}>
              <ForwardToLastIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite">
            <button type="button" className="disabled:opacity-25" disabled={favoriteIcon} onClick={() => openFavoriteDialog(t('Print.Title.MaintenanceNeedRecordDetail'), MASK_PAGE)}>
              <FavoriteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="fast">
            <button type="button" className="disabled:opacity-25" disabled={favoriteIcon} onClick={() => setFastView(MASK_PAGE, columnsVisible, columns)}>
              <PriceIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          {canSetStandardMask ? (
            <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button
                type="button"
                title={t('Common.SaveStandardMask')}
                aria-label={t('Common.SaveStandardMask')}
                onClick={confirmSaveStandardMask}
                className="flex items-center justify-center"
              >
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <div ref={elementRef}>
                <ReactTabulator
                  className="h-[calc(100vh_-_193px)]"
                  onRef={(r) => {
                    ref.current = r.current;
                  }}
                  columns={columns}
                  events={{
                    rowSelectionChanged: onSelectRow,
                    tableBuilt: () => {
                      const table = getTable();
                      if (!table) return;
                      isRestoringRef.current = true;
                      const effective = getEffectiveMask(MASK_PAGE, state);
                      if (effective?.type === 'user' && effective.userMask) {
                        applyUserMaskRowToTabulator(table, effective.userMask, {
                          setColumnsVisible,
                          clearHeaderFiltersWhenEmpty: true,
                          forceFrozenId: true,
                          applyColumnVisibilityFn: (tbl, jc, setVis) => {
                            const merged = mergeNeedRecordListColumnVisibilityFromSaved(jc);
                            setVis(merged);
                            applyNeedRecordListDataColumnVisibility(tbl, merged);
                          },
                        });
                      } else if (effective?.type === 'standard' && effective.standardMask) {
                        const m = effective.standardMask;
                        const attributes = safeParse(m.attributes, []);
                        const jsonColumns = safeParse(m.columns, {});
                        if (Array.isArray(attributes) && attributes.length > 1) {
                          table.setColumnLayout?.(sanitizeTabulatorLayoutAttributes(attributes, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS));
                        }
                        if (jsonColumns && typeof jsonColumns === 'object') {
                          const merged = mergeNeedRecordListColumnVisibilityFromSaved(jsonColumns);
                          setColumnsVisible(merged);
                          applyNeedRecordListDataColumnVisibility(table, merged);
                        }
                      }
                      setTimeout(() => {
                        isRestoringRef.current = false;
                      }, 0);
                      tableBuiltRef.current = true;
                      refreshFromStart();
                    },
                    ...getTabulatorPersistLayoutEvents(MASK_PAGE, ref, setUserMaskFilter),
                    headerFilterChanged: () => {
                      if (!isRestoringRef.current) {
                        captureHScroll();
                        refreshFromStart();
                      }
                    },
                    sortChanged: () => {
                      if (!isRestoringRef.current) {
                        captureHScroll();
                        refreshFromStart();
                      }
                    },
                    renderComplete: () => {
                      restoreHScroll();
                      if (getTable()?.getSelectedData()?.length === 0) {
                        setEditIcon(true);
                        setCopyIcon(true);
                        setDeleteIcon(true);
                      }
                    },
                  }}
                  options={{
                    layout: 'fitColumns',
                    renderHorizontal: 'virtual',
                    movableColumns: true,
                    headerSortTristate: true,
                    rowHeight: 42,
                    ajaxURL: '/api/machines/paged',
                    ajaxParams: () => ({}),
                    ajaxRequestFunc: createDedupedAjaxRequest(
                      buildRequestParams,
                      tabulatorGet,
                      inFlightRef,
                      completedCacheRef
                    ),
                    dataLoader: true,
                    dataLoaderLoading: t('Common.LoadingEllipsis'),
                    dataLoaderError: t('Common.LoadFailed'),
                    progressiveLoad: 'scroll',
                    progressiveLoadScrollMargin: 80,
                    paginationSize: pageSizePc,
                    filterMode: 'remote',
                    sortMode: 'remote',
                    pagination: false,
                    ajaxRequesting: () => {
                      if (tableBuiltRef.current && !isRestoringRef.current) captureHScroll();
                      const table = getTable();
                      if (table?.initialized) table.alert?.(t('Common.LoadingEllipsis'));
                      return true;
                    },
                    ajaxResponse: (url, params, response) => {
                      getTable()?.clearAlert?.();
                      return response;
                    },
                    ajaxError: () => {
                      getTable()?.clearAlert?.();
                    },
                    printAsHtml:true,printHeader: () => buildCanonicalPrintHeaderHtml({
                      branding: state?.branding,
                      productFallbackSrc: defaultProductLogoSrc,
                      companySettingsPrint: state?.company_settings_print,
                      moduleSettings: state?.module_settings,
                      title: t('Print.Title.MaintenanceNeedRecordList'),
                      printDateLabel: t('Print.DateLabel'),
                    }),
                    printFooter: () => buildCanonicalPrintFooterHtml({
                      companySettingsPrint: state?.company_settings_print,
                    }),
                    printRowRange: 'active',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-columns">
          <input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            type="text"
            className="block w-full px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
          />
          <button
            type="button"
            onClick={() => searchTable()}
            className="w-16 ml-8 px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600"
          >
            <SearchIcon />
          </button>
        </div>
      </Dialog>
      <Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('MaintenanceTemplateList.MaskHelp')}</p>
          <div className="flex flex-wrap gap-2 mb-2">
            <button type="button" onClick={maskSelectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.SelectAll')}</button>
            <button type="button" onClick={maskDeselectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.DeselectAllExceptId')}</button>
          </div>
          {columns.map((item) => {
            if (!item?.field || MAINTENANCE_MASK_DIALOG_SKIP_FIELDS.has(item.field)) return null;
            return (
              <div key={item.field} className="flex flex-row border border-black rounded-md p-2 m-1">
                <MaintenanceMaskDialogCheckbox field={item.field} inputId={`mnrl-mask-${item.field}`} title={item.title || item.field} columnsVisible={columnsVisible} onChange={setMask} />
                <label htmlFor={`mnrl-mask-${item.field}`} className={`ml-2 ${item.field === 'id' ? '' : 'cursor-pointer'}`}>{item.title}</label>
              </div>
            );
          })}
          <button type="button" onClick={saveUserMaskColumns} className="m-1 px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-zinc-700">{t('Common.SaveUpper')}</button>
        </div>
      </Dialog>
    </>
  );
}
